<template>
  <v-container class="py-8 px-4" fluid>
    <!-- editor -->
    <AssistantEditor
      v-if="editorAgent"
      :agent="editorAgent"
      :is-draft="!!draftAgent"
      @back="onBack"
      @saved="onSaved"
      @deleted="onDeleted"
    />

    <!-- list view -->
    <template v-else>
      <h1 class="text-h4 font-weight-bold mb-2">Assistants</h1>
      <p class="text-body-1 text-medium-emphasis mb-8" style="max-width: 580px">
        Your AI assistants — each one a model with a job, the instructions it follows and the tools it
        can use. Set them up in plain language here; switch to Advanced mode anytime for the full controls.
      </p>

      <!-- filter/sort toolbar (same controls as Advanced Agents, shown once there are assistants) -->
      <div v-if="agents.length" class="d-flex align-center flex-wrap ga-4 mb-6">
        <v-btn-toggle v-model="sortOrder" mandatory density="compact">
          <v-btn icon="mdi-sort-variant-remove" value="default" size="small" title="Default Order"></v-btn>
          <v-btn icon="mdi-sort-alphabetical-ascending" value="asc" size="small" title="Sort Ascending"></v-btn>
          <v-btn icon="mdi-sort-alphabetical-descending" value="desc" size="small" title="Sort Descending"></v-btn>
        </v-btn-toggle>
        <div class="ms-filter">
          <v-combobox
            v-model="search"
            :items="configList"
            :return-object="false"
            item-title="name"
            item-value="name"
            clearable
            variant="outlined"
            density="compact"
            label="Filter assistants"
            hide-details
          >
            <template v-slot:append>
              <span class="text-caption text-disabled">{{ filteredAgents.length }}</span>
            </template>
          </v-combobox>
        </div>
      </div>

      <v-row>
        <!-- new -->
        <v-col cols="12" md="6" lg="4">
          <v-card
            variant="outlined"
            rounded="lg"
            height="100%"
            min-height="168"
            class="pa-6 d-flex flex-column justify-center ga-2 ms-add"
            @click="newAssistant"
          >
            <v-avatar color="primary" variant="tonal" rounded="lg" size="44">
              <v-icon icon="mdi-plus" />
            </v-avatar>
            <span class="text-subtitle-1 font-weight-bold">New assistant</span>
            <span class="text-caption text-disabled">Build one your way — or add a brain</span>
          </v-card>
        </v-col>

        <!-- existing (reuses the app's AgentSummaryCard — an assistant IS an agent) -->
        <v-col v-for="a in filteredAgents" :key="a.config.id" cols="12" md="6" lg="4">
          <AgentSummaryCard
            :agent="a"
            variant="desktop"
            :model-configs-by-id="modelConfigsById"
            :username="username"
            @edit="openId = a.config.id"
          />
        </v-col>
      </v-row>
    </template>

    <AssistantKindDialog v-model="picker" @pick="onPickKind" />
    <AddModelDialog v-model="addModelOpen" :connected="connectedSet" @created="onModelCreated" />
  </v-container>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import AgentSummaryCard from '@/components/cards/AgentSummaryCard.vue'
import AssistantEditor from '@/components/agentpages/assistants/AssistantEditor.vue'
import AssistantKindDialog from '@/components/agentpages/assistants/AssistantKindDialog.vue'
import AddModelDialog from '@/components/agentpages/assistants/AddModelDialog.vue'
import { ASSISTANT_OPENER } from '@/data/assistants'
import type { AssistantKind } from '@/data/assistants'
import { connectedProviderSet, filterSortAssistants } from '@/utils/assistants'
import type { AssistantSortOrder } from '@/utils/assistants'
import { newId } from '@/utils/general'
import { getDashboardTabPath } from '@/router/dashboardTabs'
import type { UserAgentConfig, AgentConfig } from '@/types/agents'
import type { ModelConfig } from '@/types/models'
import type { ProviderConfig } from '@/types/providers'

@Options({
  components: { AgentSummaryCard, AssistantEditor, AssistantKindDialog, AddModelDialog },
  computed: {
    ...mapGetters([
      'agentConfigsList',
      'modelConfigsById',
      'providerConfigsList',
      'username',
      'configAgentSlug',
      'selectedTab'
    ])
  },
  watch: {
    // The tab stays mounted in the dashboard v-window, so re-fetch when re-entering Assistants so an
    // agent created elsewhere (e.g. via the Chat config agent) appears without a reload.
    selectedTab(value: string) {
      if (value === 'assistants') {
        void (this as AssistantsView).refresh()
      }
    }
  }
})
export default class AssistantsView extends Vue {
  agentConfigsList!: UserAgentConfig[]
  modelConfigsById!: Record<string, { config: ModelConfig }>
  providerConfigsList!: ProviderConfig[]
  username!: string
  configAgentSlug!: string | null
  selectedTab!: string

  openId: string | null = null
  picker = false
  addModelOpen = false
  search: string | null = null
  sortOrder: AssistantSortOrder = 'default'
  // A brand-new, unsaved agent being created via "Build it myself". Rendered in the editor like an
  // existing assistant; AssistantEditor.save() creates the real record through setAgentConfig.
  draftAgent: AgentConfig | null = null

  async mounted() {
    await this.refresh()
  }

  async refresh() {
    await Promise.all([
      // Assistants is the simple-mode lens over both Advanced Models and Advanced Agents, so refresh
      // the same list sources those tabs depend on whenever this tab is opened.
      this.$store.dispatch('fetchProviders'),
      this.$store.dispatch('fetchModels'),
      this.$store.dispatch('fetchModelConfigs'),
      this.$store.dispatch('fetchPublishedAgents'),
      this.$store.dispatch('fetchSharedAgents'),
      // Voices are otherwise only fetched by the Models tab, which simple mode hides — without this
      // the editor's voice dropdown would always be empty for simple-mode users. Fetched
      // unconditionally (matching the Models tab) because the in-memory store starts empty.
      this.$store.dispatch('fetchVoices')
    ])
  }

  get agents(): UserAgentConfig[] {
    return this.agentConfigsList ?? []
  }

  // The list actually rendered: the agents passed through the shared Advanced filter/sort.
  get filteredAgents(): UserAgentConfig[] {
    return filterSortAssistants(this.agents, this.search, this.sortOrder)
  }

  // Autocomplete suggestions for the filter combobox (the agent configs, matched on name).
  get configList(): AgentConfig[] {
    return this.agents.map((a) => a.config)
  }

  get connectedSet(): Set<string> {
    return connectedProviderSet(this.providerConfigsList || [])
  }

  get openAgent(): AgentConfig | undefined {
    return this.agents.find((a) => a.config.id === this.openId)?.config
  }

  // What the editor renders: a brand-new draft when building one by hand, else the selected assistant.
  get editorAgent(): AgentConfig | undefined {
    return this.draftAgent ?? this.openAgent
  }

  newAssistant() {
    this.picker = true
  }

  // Three ways to add: mount a new model (its own surface — model creation is kept out of the assistant
  // editor), build an assistant by hand, or describe it to the configuration assistant in Chat.
  onPickKind(kind: AssistantKind) {
    this.picker = false
    if (kind === 'llm') {
      this.addModelOpen = true
    } else if (kind === 'manual') {
      this.openId = null
      this.draftAgent = this.makeBlankAgent()
    } else if (kind === 'video') {
      // Stage 1 (pending): wire up the Video To Agent flow here.
      this.$store.dispatch('toggleVideoToAgentDialog', true)
    } else {
      this.openConfigChatForAssistant()
    }
  }

  // A blank agent for the "build it myself" editor. Mirrors the shape AgentCard builds for a new agent
  // (newId + defaults); the real record is created on save via setAgentConfig.
  makeBlankAgent(): AgentConfig {
    return {
      id: newId(),
      timestamp: Date.now(),
      name: '',
      description: '',
      systemPrompt: '',
      model: '',
      context: [],
      temperature: 0.2,
      maxTokens: -1,
      modelOptions: {}
    }
  }

  // Hand off to the configuration assistant, which builds a real agent conversationally.
  openConfigChatForAssistant() {
    const slug = this.configAgentSlug
    if (slug) {
      this.$store.dispatch('setPendingChatMessage', ASSISTANT_OPENER)
      this.$store.dispatch('setPendingChatAgent', { slug })
      this.$store.dispatch('setTab', 'chat')
      const path = getDashboardTabPath('chat')
      if (this.$route.path !== path) {
        this.$router.push(path)
      }
    } else {
      this.$store.dispatch('setConfigChatSeed', ASSISTANT_OPENER)
      this.$store.dispatch('toggleConfigChat', true)
    }
  }

  // After mounting a new brain, refresh so it's immediately selectable in the editor's BrainPicker.
  onModelCreated() {
    void this.refresh()
  }

  onBack() {
    this.openId = null
    this.draftAgent = null
  }

  onSaved() {
    this.openId = null
    this.draftAgent = null
    void this.refresh()
  }

  // Store state is already updated by deleteAgentConfig; refresh keeps published/model lists in sync.
  onDeleted() {
    this.openId = null
    this.draftAgent = null
    void this.refresh()
  }
}
</script>

<style scoped lang="scss">
.ms-filter {
  width: 320px;
  max-width: 100%;
}

.ms-add {
  cursor: pointer;
  border-style: dashed;
  transition: border-color 0.16s;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
  }
}
</style>
